import React from "react";

// Source
import StoreRole from "../store/Role";
import StoreLocalization from "../store/Localization";
import StorePreference from "../store/Preference";
import ControllerAlert from "./Alert";
import * as HelperSrc from "../HelperSrc";
import * as ApiUser from "../api/User";
import * as ModelUser from "../model/User";
import * as ModelRole from "../model/Role";
import * as ModelLocalization from "../model/Localization";
import * as ModelPreference from "../model/Preference";

export default class ControllerMyProfile {
    // Variable
    private props: ModelUser.Iprops;

    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private elementBottom = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private validationForm = React.useRef<ModelUser.Idata | undefined>(undefined);

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getUser: ModelUser.Idata | undefined;
    setUser: React.Dispatch<React.SetStateAction<ModelUser.Idata | undefined>>;

    getPassword: string | undefined;
    setPassword: React.Dispatch<React.SetStateAction<string | undefined>>;

    getValidationForm: ModelUser.Idata | undefined;
    setValidationForm: React.Dispatch<React.SetStateAction<ModelUser.Idata | undefined>>;

    // Method
    constructor(props: ModelUser.Iprops, isRendered: boolean) {
        this.props = props;

        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getUser, setUser] = React.useState<ModelUser.Idata | undefined>(undefined);
        this.getUser = getUser;
        this.setUser = setUser;

        const [getPassword, setPassword] = React.useState<string | undefined>(undefined);
        this.getPassword = getPassword;
        this.setPassword = setPassword;

        const [getValidationForm, setValidationForm] = React.useState<ModelUser.Idata | undefined>(this.validationForm.current);
        this.getValidationForm = getValidationForm;
        this.setValidationForm = setValidationForm;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_my_profile", () => {
                this.initializeHtmlElement();
            });

            this.initializeUser();

            this.apiValidationForm();

            HelperSrc.storageEventListener("localization", () => {
                this.apiValidationForm();
            });
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
            this.storagePreferenceData.current = this.storePreference.storageGet();
        }, [this.storageRoleData.current, this.storageLocalizationData.current, this.storagePreferenceData.current]);
    };

    clickChange = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const element = event.target as HTMLElement;

        const checkForm = HelperSrc.checkForm(element, this.validationForm.current);

        if (!checkForm) {
            return;
        }

        if (this.props.dataMain) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                profileChange: true
            }));

            ApiUser.updateDataMyProfile(
                this.props.dataMain.id,
                this.readHtmlElementValue("username"),
                this.readHtmlElementValue("password"),
                this.readHtmlElementValue("confirm_password"),
                this.readHtmlElementValue("first_name"),
                this.readHtmlElementValue("last_name"),
                this.readHtmlElementValue("email"),
                (response) => {
                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        profileChange: false
                    }));

                    if (response instanceof Error || response.message.error) {
                        if (response instanceof Error) {
                            HelperSrc.writeLog("error", response.message);
                        } else {
                            this.controllerAlert.show("error", response.message.error, false);
                        }

                        return;
                    }

                    this.controllerAlert.show("success", response.message.success);
                }
            );
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementBottom.current = document.querySelector<HTMLElement>(".bottom");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_my_profile");
        }
    };

    private readHtmlElementValue = (tag: string): string => {
        const element = document.querySelector<HTMLInputElement>(`[name='${tag}']`);

        return element ? element.value : "";
    };

    private apiValidationForm = (): void => {
        ApiUser.validationForm((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.validationForm.current = response.data.result as unknown as ModelUser.Idata;
            this.setValidationForm(this.validationForm.current);

            if (this.elementBottom.current) {
                HelperSrc.validationFormHelper(this.elementBottom.current, this.validationForm.current);
            }
        });
    };

    private initializeUser = (): void => {
        if (this.props.dataMain) {
            const checkboxRoleList = HelperSrc.convertRoleToCheckbox(this.storageRoleData.current, this.props.dataMain.role);

            const user = {
                id: this.props.dataMain.id,
                username: this.props.dataMain.username,
                first_name: this.props.dataMain.first_name,
                last_name: this.props.dataMain.last_name,
                email: this.props.dataMain.email,
                last_activity: this.props.dataMain.last_activity,
                is_active: this.props.dataMain.is_active,
                auth_type: this.props.dataMain.auth_type,
                ...checkboxRoleList
            } as ModelUser.Idata;

            this.setUser(user);
        }
    };
}
